import React, { useEffect, useState, useCallback } from 'react'
import { Container, Row, Col, Card, Form, Button, Spinner } from '@themesberg/react-bootstrap'
import { toast } from 'react-toastify'
import { useCookies } from 'react-cookie'
import { getDepositWithdrawSetting, updateDepositWithdrawSetting } from '../../../controller/api/setting'

const DEFAULT_CONFIG = {
  depositEnabled: true,
  withdrawEnabled: true,
  withdrawMinPerTransaction: 200000,
  withdrawMaxPerTransaction: 0,
  withdrawMaxPerDay: 0,
}

const LIMIT_FIELDS = [
  {
    field: 'withdrawMinPerTransaction',
    label: 'Rút tối thiểu mỗi lần (VNĐ)',
    hint: 'Áp dụng cho rút về tài khoản ngân hàng',
  },
  {
    field: 'withdrawMaxPerTransaction',
    label: 'Rút tối đa mỗi lần (VNĐ)',
    hint: '0 = không giới hạn',
  },
  {
    field: 'withdrawMaxPerDay',
    label: 'Rút tối đa mỗi ngày (VNĐ)',
    hint: '0 = không giới hạn, tính tổng các lệnh rút trong ngày',
  },
]

const toNonNegativeInt = (value, fallback) => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed < 0) return fallback
  return Math.floor(parsed)
}

const DepositWithdrawSettings = () => {
  const [config, setConfig] = useState(DEFAULT_CONFIG)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [cookies] = useCookies(['lottmekong'])
  const token = cookies.lottmekong

  const hydrateConfig = useCallback((incoming) => {
    const next = {
      ...DEFAULT_CONFIG,
      ...(incoming || {}),
    }
    setConfig(next)
  }, [])

  const fetchSetting = useCallback(async () => {
    if (!token) {
      toast.error('Phiên đăng nhập không hợp lệ. Vui lòng đăng nhập lại.')
      return
    }
    setLoading(true)
    try {
      const data = await getDepositWithdrawSetting(token)
      hydrateConfig(data)
    } catch (error) {
      console.error('Fetch Deposit/Withdraw setting error', error)
      if (error.response?.status === 401) {
        toast.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.')
      } else {
        toast.error('Không thể tải cấu hình nạp/rút tiền. Vui lòng thử lại.')
      }
    } finally {
      setLoading(false)
    }
  }, [hydrateConfig, token])

  useEffect(() => {
    fetchSetting()
  }, [fetchSetting])

  const handleToggle = (field) => () => {
    setConfig((prev) => ({
      ...prev,
      [field]: !prev[field],
    }))
  }

  const handleLimitChange = (field) => (event) => {
    const { value } = event.target
    setConfig((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!token) {
      toast.error('Phiên đăng nhập không hợp lệ. Vui lòng đăng nhập lại.')
      return
    }

    setSaving(true)
    try {
      const payload = {
        depositEnabled: config.depositEnabled,
        withdrawEnabled: config.withdrawEnabled,
        withdrawMinPerTransaction: toNonNegativeInt(
          config.withdrawMinPerTransaction,
          DEFAULT_CONFIG.withdrawMinPerTransaction
        ),
        withdrawMaxPerTransaction: toNonNegativeInt(
          config.withdrawMaxPerTransaction,
          DEFAULT_CONFIG.withdrawMaxPerTransaction
        ),
        withdrawMaxPerDay: toNonNegativeInt(
          config.withdrawMaxPerDay,
          DEFAULT_CONFIG.withdrawMaxPerDay
        ),
      }

      const updated = await updateDepositWithdrawSetting(token, payload)
      hydrateConfig(updated)
      toast.success('Đã lưu cấu hình nạp/rút tiền thành công!')
    } catch (error) {
      console.error('Update Deposit/Withdraw setting error', error)
      if (error.response?.status === 401) {
        toast.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.')
      } else {
        toast.error('Không thể lưu cấu hình nạp/rút tiền. Vui lòng thử lại.')
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Container fluid className="py-4">
      <Row className="justify-content-center">
        <Col lg={8}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-0">Cấu hình Nạp tiền & Rút tiền</h4>
                <small className="text-muted">
                  Bật/tắt chức năng và thiết lập hạn mức rút tiền cho khách hàng
                </small>
              </div>
              <div className="d-flex gap-2">
                <Button variant="outline-secondary" size="sm" onClick={fetchSetting} disabled={loading || saving}>
                  {loading ? <Spinner animation="border" size="sm" /> : 'Tải lại'}
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-4 d-flex justify-content-between align-items-center">
                  <div>
                    <Form.Label className="mb-0 fw-bold">Nạp tiền</Form.Label>
                    <div className="text-muted small">
                      {config.depositEnabled ? 'Đang bật - Khách hàng có thể nạp tiền' : 'Đang tắt - Hiển thị thông báo bảo trì'}
                    </div>
                  </div>
                  <Form.Check
                    type="switch"
                    id="deposit-toggle"
                    checked={config.depositEnabled}
                    onChange={handleToggle('depositEnabled')}
                    disabled={loading || saving}
                  />
                </Form.Group>

                <Form.Group className="mb-4 d-flex justify-content-between align-items-center">
                  <div>
                    <Form.Label className="mb-0 fw-bold">Rút tiền</Form.Label>
                    <div className="text-muted small">
                      {config.withdrawEnabled ? 'Đang bật - Khách hàng có thể rút tiền' : 'Đang tắt - Hiển thị thông báo bảo trì'}
                    </div>
                  </div>
                  <Form.Check
                    type="switch"
                    id="withdraw-toggle"
                    checked={config.withdrawEnabled}
                    onChange={handleToggle('withdrawEnabled')}
                    disabled={loading || saving}
                  />
                </Form.Group>

                <hr />

                <h5 className="mb-3">Hạn mức rút tiền</h5>

                {LIMIT_FIELDS.map(({ field, label, hint }) => (
                  <Form.Group className="mb-4" key={field}>
                    <Form.Label className="fw-bold">{label}</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      step={1000}
                      value={config[field]}
                      onChange={handleLimitChange(field)}
                      disabled={loading || saving}
                    />
                    <div className="text-muted small mt-1">{hint}</div>
                  </Form.Group>
                ))}

                <div className="d-flex justify-content-end gap-2">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={saving}
                  >
                    {saving ? <Spinner animation="border" size="sm" /> : 'Lưu cấu hình'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

export default DepositWithdrawSettings
